import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { INTERNAL_CONSTANTS } from '@constants/internalConstants'
import { assetUrl } from '@utils/assetUrl'
import NEGroupLogo from '../../media/animation/NEGroupLogo'

const CRASH_BG_IMAGE = assetUrl('images/background-images/CRASH_BG_IMAGE.webp')
const DEFENDER_BG_IMAGE = assetUrl('images/background-images/DEFENDER_BG_IMAGE.webp')
const HOWZZAT_BG_IMAGE = assetUrl('images/background-images/HOWZZAT_BG_IMAGE.webp')

// Minimum time (ms) the boot loader stays on screen, regardless of how fast
// auth resolves — keeps the branded loader visible long enough to read rather
// than flashing by. Override per-mount via the `minDurationMs` prop.
const DEFAULT_MIN_DURATION_MS = 3500

// How long the completed state (100% + launch message) stays visible after both
// the minimum duration and auth success are met, before handing off to the game.
const DEFAULT_COMPLETION_HOLD_MS = 800

const LOADING_MESSAGES = [
  'Loading essential game assets and dependencies..',
  'Preparing the application environment and settings..',
  'Initializing user session and system components..',
  'Configuring game logic and runtime parameters..',
  'Connecting securely to the game server..',
  'Syncing the latest game data and updates..',
  'Applying display settings and user preferences..',
  'Rendering interface elements and animations..',
  'Running final checks and optimizations..',
]

// Shown while the bar holds at 90% and auth is still pending.
const NETWORK_WAIT_MESSAGES = [
  'Still connecting to the server. This may take a few moments...',
  'Optimizing connection... please wait.',
  'Verifying server response...',
  'Retrying data sync...',
  'Hold on — stabilizing the network link...',
  'Finalizing handshake with the game server...',
]

// Shown while the bar holds at 90% and auth has SUCCEEDED but we're still
// waiting on the minimum on-screen duration — so we never say "connecting"
// after we're actually connected.
const FINALIZING_MESSAGES = [
  'Almost ready — finalizing your session...',
  'Wrapping up the final touches...',
  'Preparing your table...',
  'Just a moment...',
]

// Progress animation cadence. The bar climbs in CLIMB_STEP chunks every
// PROGRESS_TICK_MS (the 0.5s CSS width transition glides between them),
// reaching 90% in ~2.4s, then holds at 90% rotating a message every
// HOLD_MESSAGE_EVERY ticks until completion.
const PROGRESS_TICK_MS = 400
const CLIMB_STEP = 15
const HOLD_MESSAGE_EVERY = 3

const VARIENT_IMAGE = {
  DEFENDER: DEFENDER_BG_IMAGE,
  CRASH: CRASH_BG_IMAGE,
  HOWZZAT: HOWZZAT_BG_IMAGE,
}

const GameBootLoader = ({
  minDurationMs = DEFAULT_MIN_DURATION_MS,
  completionHoldMs = DEFAULT_COMPLETION_HOLD_MS,
  onComplete,
}) => {
  const authenticationStatus = useSelector((state) => state.session.authenticationStatus)

  const gameCode = useSelector((state) => state.launchParams.params.gameCode)
  const backgroundImage = VARIENT_IMAGE[gameCode]

  const [message, setMessage] = useState('')
  const [progressPercent, setProgressPercent] = useState(0)
  const [showFooter, setShowFooter] = useState(false)
  const [minDurationElapsed, setMinDurationElapsed] = useState(false)

  const intervalRef = useRef(null)
  const progressRef = useRef(0) // current bar % (drives state without re-reading it)
  const loadingMsgIndexRef = useRef(0) // next LOADING_MESSAGES index while climbing
  const holdMsgIndexRef = useRef(0) // rotating index for the 90%-hold message pool
  const holdTickRef = useRef(0) // ticks spent holding at 90% (paces hold messages)
  const completedRef = useRef(false)

  // Canvas load state (driven by the canvas bridge). The loader now waits on the
  // game canvas too — not just auth — and blends its real progress into the bar.
  const {
    ready: canvasReady,
    progress: canvasProgress,
    error: canvasError,
  } = useSelector((state) => state.ui.canvas)

  const authSucceeded = authenticationStatus === INTERNAL_CONSTANTS.AUTHENTICATION_STATUS.SUCCEEDED
  const authResolved =
    authSucceeded || authenticationStatus === INTERNAL_CONSTANTS.AUTHENTICATION_STATUS.FAILED
  // `error` is a safety valve so a misbehaving canvas can't hang boot forever.
  const canvasResolved = canvasReady || canvasError

  // "Ready" (success copy + 100%) needs BOTH auth success and the canvas ready.
  const isReady = authSucceeded && canvasReady
  const isResolved = authResolved && canvasResolved

  // 100% and the launch message only appear once we're ready *and* the minimum
  // on-screen duration is met. Until both hold we keep the bar climbing / holding
  // with live messages — never a premature "launching".
  const isComplete = isReady && minDurationElapsed

  // Latest values mirrored into refs so the long-lived interval/handoff effects
  // can read them without re-subscribing every render (PlayZone passes a fresh
  // inline onComplete each render).
  const onCompleteRef = useRef(onComplete)
  const isReadyRef = useRef(isReady)
  useEffect(() => {
    onCompleteRef.current = onComplete
    isReadyRef.current = isReady
  })

  // Hold the loader for at least `minDurationMs` so the animated logo can play
  // through even when auth resolves almost instantly.
  useEffect(() => {
    const timer = setTimeout(() => setMinDurationElapsed(true), minDurationMs)
    return () => clearTimeout(timer)
  }, [minDurationMs])

  // Drive the fake-progress animation until we're fully complete. The bar climbs
  // to 90% (cycling LOADING_MESSAGES), then holds at 90% rotating a wait/finalize
  // message — regardless of auth status — so it never freezes while we wait out
  // the minimum duration.
  useEffect(() => {
    if (isComplete) return

    const tick = () => {
      setShowFooter(true)

      if (progressRef.current < 90) {
        progressRef.current = Math.min(progressRef.current + CLIMB_STEP, 90)
        setProgressPercent(progressRef.current)
        const index = Math.min(loadingMsgIndexRef.current, LOADING_MESSAGES.length - 1)
        setMessage(LOADING_MESSAGES[index])
        loadingMsgIndexRef.current += 1
        return
      }

      // Holding at 90% — rotate a message on a slower cadence until complete.
      // Use "finalizing" copy once auth has succeeded, "connecting" copy while
      // it's still pending.
      if (holdTickRef.current % HOLD_MESSAGE_EVERY === 0) {
        const pool = isReadyRef.current ? FINALIZING_MESSAGES : NETWORK_WAIT_MESSAGES
        setMessage(pool[holdMsgIndexRef.current % pool.length])
        holdMsgIndexRef.current += 1
      }
      holdTickRef.current += 1
    }

    tick() // paint the first step immediately rather than after one interval
    intervalRef.current = setInterval(tick, PROGRESS_TICK_MS)
    return () => clearInterval(intervalRef.current)
  }, [isComplete])

  // Blend the real canvas progress into the animated climb (whichever is further
  // along), capped below 100 until we're actually complete so the bar never
  // reads "full" while we're still waiting on auth or the minimum duration.
  const displayProgress = isComplete
    ? 100
    : Math.min(95, Math.max(progressPercent, Math.round(canvasProgress * 100)))
  const displayMessage = isComplete ? `Everything's set — launching the game.` : message

  // Hand off to the game once auth has resolved AND the minimum duration has
  // elapsed — so a fast auth still lets the logo finish animating. On success we
  // hold briefly on the 100% state so it's actually visible; then fire
  // `onComplete` exactly once. (Failure hands off without the success state.)
  useEffect(() => {
    if (completedRef.current) return
    if (isResolved && minDurationElapsed) {
      completedRef.current = true
      const hold = isReady ? completionHoldMs : 0
      const timer = setTimeout(() => onCompleteRef.current?.(), hold)
      return () => clearTimeout(timer)
    }
  }, [isReady, isResolved, minDurationElapsed, completionHoldMs])

  return (
    <div
      className="game-boot-loader-outer-container"
      data-game-code={gameCode}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className="game-boot-loader-inner-container">
        <div className="game-boot-loader-logo-container">
          <NEGroupLogo />
        </div>
        {showFooter && (
          <div className="game-boot-loader-bottom-container">
            <div className="game-boot-loader-progress-bar-container">
              <div
                className="game-boot-loader-progress-bar-fill"
                style={{ width: `${displayProgress}%` }}
              />
            </div>
            <div className="game-boot-loader-loading-message-container">{displayMessage}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GameBootLoader
